export interface AddEmployeeState {
    selectedEmployees: Map<number, number>;
    selectableEmployees: Map<number, number>;
    selectAllLoading?: boolean;
    clickedButSelectAllWasLoading?: boolean;
}

export type CheckSingleEmployeeAction = {
    type: 'CheckSingleEmployee';
    employeeToEmployment: EmployeeIdToEmploymentId;
};

export type UncheckSingleEmployeeAction = {
    type: 'UncheckSingleEmployee';
    employeeToEmployment: EmployeeIdToEmploymentId;
};

export type SelectAllAction = {
    type: 'SelectAll';
};

export type UncheckAllEmployeesAction = {
    type: 'UncheckAllEmployees';
};

export type EmployeeIdToEmploymentId = {
    employeeId: number;
    employmentId: number;
};

export type CreateMapAction = {
    type: 'CreateMap';
    employeesToEmployments: Array<EmployeeIdToEmploymentId>;
};

export type UpdateMapAction = {
    type: 'UpdateMap';
    employeeToEmployment: EmployeeIdToEmploymentId;
};

export type SelectAllLoadingAction = {
    type: 'SelectAllLoading';
    value: boolean;
};

export type AddEmployeeAction =
    | CheckSingleEmployeeAction
    | UncheckSingleEmployeeAction
    | UncheckAllEmployeesAction
    | CreateMapAction
    | SelectAllAction
    | UpdateMapAction
    | SelectAllLoadingAction;

export function addEmploymentReducer(state: AddEmployeeState, action: AddEmployeeAction): AddEmployeeState {
    switch (action.type) {
        case 'CheckSingleEmployee':
            return {
                ...state,
                selectedEmployees: state.selectedEmployees.set(
                    action.employeeToEmployment.employeeId,
                    action.employeeToEmployment.employmentId,
                ),
            };

        case 'UncheckSingleEmployee': {
            const selectedEmployees = new Map(state.selectedEmployees);
            selectedEmployees.delete(action.employeeToEmployment.employeeId);
            return {
                ...state,
                selectedEmployees,
            };
        }
        case 'SelectAll':
            //if you selectAll when it is loading, you will have to selectAll at another time
            if (state.selectAllLoading) {
                return {
                    ...state,
                    clickedButSelectAllWasLoading: true,
                };
            }
            return {
                ...state,
                selectedEmployees: state.selectableEmployees,
                clickedButSelectAllWasLoading: false,
            };
        case 'UncheckAllEmployees':
            return {
                ...state,
                selectedEmployees: new Map<number, number>(),
            };
        case 'CreateMap': {
            const map = new Map<number, number>();
            action.employeesToEmployments.forEach((element) => map.set(element.employeeId, element.employmentId));
            return {
                ...state,
                selectableEmployees: map,
            };
        }
        case 'UpdateMap': {
            const selectedEmployees = state.selectedEmployees;
            if (state.selectedEmployees.has(action.employeeToEmployment.employeeId)) {
                state.selectedEmployees.set(
                    action.employeeToEmployment.employeeId,
                    action.employeeToEmployment.employmentId,
                );
            }

            const selectableEmployees = state.selectableEmployees.set(
                action.employeeToEmployment.employeeId,
                action.employeeToEmployment.employmentId,
            );

            return {
                ...state,
                selectedEmployees,
                selectableEmployees,
            };
        }
        case 'SelectAllLoading':
            return {
                ...state,
                selectAllLoading: action.value,
            };
        default:
            return state;
    }
}

export function stateContainsEmployee(state: AddEmployeeState, id: number) {
    return state.selectedEmployees.has(id);
}

export function stateContainsAllEmployees(state: AddEmployeeState) {
    return state.selectableEmployees.size > 0 && state.selectedEmployees.size === state.selectableEmployees.size;
}

export function stateContainsNoEmployees(state: AddEmployeeState) {
    return state.selectedEmployees.size === 0;
}

export function stateContainsSomeButNotAllEmployees(state: AddEmployeeState) {
    return !stateContainsNoEmployees(state) && !stateContainsAllEmployees(state);
}

export enum SelectAllState {
    SPINNER,
    DISABLED,
    ENABLED,
}

export function stateSelectAllState(state: AddEmployeeState): SelectAllState {
    if (state.selectAllLoading && state.clickedButSelectAllWasLoading) {
        return SelectAllState.SPINNER;
    }
    return SelectAllState.ENABLED;
}
